import axios from 'axios'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { DELIVERY_DATETIME_LABELS } from '../constants/order-details'
import { baseUrlFrontApi, ffServicesApiUrl } from '~/js/templates/index/helpers'

dayjs.extend(customParseFormat)
const weekDays = ['日', '月', '火', '水', '木', '金', '土']
const SHIPPING_INSTRUCTIONS = {
  furniture: '発送準備中(家具)',
  misc: '発送準備中(雑貨)',
  curtain: '発送準備中(オーダーカーテン)',
}

const init = async function () {
  const ddContainer = document.querySelectorAll(
    '[data-update-delivery-datetime]'
  )
  if (!ddContainer) return false
  const raw = document.querySelector('[data-order-details-json]').innerHTML
  const { shippingAddress, tags, attributes, isUpdated, id, shukka_shiji } =
    JSON.parse(raw)

  /**
   * `YYYY-MM-DD`形式の日付文字列を受け取り、日本語の日付文字列に変換して返す。
   * 受け取った値が`undefined`または`YYYY-MM-DD`形式ではない場合、`undefined`を返す。
   * @param {string | undefined} dateString `YYYY-MM-DD`形式の日付文字列 （例: `2024-03-21`）
   * @returns {string | undefined} 日本語の日付文字列 （例:`2024年03月21(木)`）
   */
  function formatISODateToJapanese(dateString) {
    const dateObject = new Date(dateString)
    if (isNaN(dateObject.getTime())) return

    const year = dateObject.getFullYear()
    const month = (dateObject.getMonth() + 1).toString().padStart(2, '0')
    const date = dateObject.getDate().toString().padStart(2, '0')
    const DAYS_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土']
    const day = DAYS_OF_WEEK[dateObject.getDay()]
    return `${year}年${month}月${date}日(${day})`
  }

  /**
   * ノートアトリビュート`雑貨配送希望日`、`お届け希望日`（オーダーカーテン）、`家具配送希望日`のいずれかの値を受け取り、
   * 受け取った値が`2024年03月21(木)`形式または`指定なし`ならその値をそのまま返す。
   * 受け取った値が`2024-03-21`形式の場合は`formatISODateToJapanese`関数で`2024年03月21(木)`形式に変換して返す。
   * @param {string} noteAttributeDeliveryDate ノートアトリビュート`雑貨配送希望日`または`お届け希望日`（オーダーカーテン）または`家具配送希望日`の値
   * @returns {string} `2024年03月21(木)`形式の日付文字列または`指定なし`
   */
  function formatNoteAttributeDeliveryDate(noteAttributeDeliveryDate) {
    return (
      formatISODateToJapanese(noteAttributeDeliveryDate) ||
      noteAttributeDeliveryDate
    )
  }

  const attributeValue = {
    misc: {
      date: formatNoteAttributeDeliveryDate(attributes['雑貨配送希望日']),
      time: attributes['雑貨配送希望時間帯'],
      carrierKey: attributes['雑貨配送業者キー'],
    },
    curtain: {
      date: formatNoteAttributeDeliveryDate(attributes['お届け希望日']),
      time: attributes['配送希望時間帯'],
      carrierKey: attributes['オーダーカーテン配送業者キー'],
    },
    furniture: {
      date: formatNoteAttributeDeliveryDate(attributes['家具配送希望日']),
      time: attributes['家具配送希望時間帯'],
      carrierKey: attributes['家具配送業者キー'],
    },
  }

  ddContainer.forEach(async (element) => {
    const { type, isAirShippingDisabled } = element.dataset

    const hasAttributes = Object.keys(attributes).some((x) =>
      DELIVERY_DATETIME_LABELS[type].includes(x)
    )
    const hasShippingInstructions = tags.includes(SHIPPING_INSTRUCTIONS[type])

    if (!hasAttributes) {
      element.parentNode.remove()
      return
    }

    const carrier_ =
      type == 'misc'
        ? attributeValue.misc.carrierKey
        : attributeValue.furniture.carrierKey

    const deliveryDateTimeOptionsMap = await getDeliveryDateTimeOptions(
      globalThis,
      baseUrlFrontApi,
      ffServicesApiUrl,
      shippingAddress,
      carrier_,
      type,
      isAirShippingDisabled
    )

    const dateTimeOptions = deliveryDateTimeOptionsMap[type]

    if (!(dateTimeOptions && !hasShippingInstructions && !shukka_shiji)) return

    const dateContainer = element.querySelector('[data-date]')
    const timeContainer = element.querySelector('[data-time]')
    const updateButton = element.querySelector(
      '.delivery-datetime-update-button'
    )

    const timeSelector = buildTimeSelector(
      dateTimeOptions,
      attributes[DELIVERY_DATETIME_LABELS[type][0]],
      attributes[DELIVERY_DATETIME_LABELS[type][1]]
    )

    const dateSelector = buildDateSelector(
      dateTimeOptions,
      attributes[DELIVERY_DATETIME_LABELS[type][0]]
    )

    const originalValue = attributeValue[type]

    dateSelector.setAttribute('disabled', true)
    timeSelector.setAttribute('disabled', true)
    dateContainer.innerHTML = ''
    timeContainer.innerHTML = ''
    dateContainer.append(dateSelector)
    timeContainer.append(timeSelector)

    if (isUpdated[type]) return

    dateSelector.removeAttribute('disabled')
    timeSelector.removeAttribute('disabled')

    const normalizeDateValue = (value) => {
      if (!value) return value

      const isoDate = dayjs(value, 'YYYY-MM-DD', true)
      if (isoDate.isValid()) return isoDate.format('YYYY-MM-DD')

      const japaneseDate = dayjs(value, 'YYYY年MM月DD日(dd)', 'ja', true)
      if (japaneseDate.isValid()) return japaneseDate.format('YYYY-MM-DD')

      return value
    }

    const syncUpdateButtonState = () => {
      const selectedDate = dateContainer.querySelector('select')?.value
      const selectedTime = timeContainer.querySelector('select')?.value
      const isChanged =
        normalizeDateValue(originalValue.date) !==
          normalizeDateValue(selectedDate) ||
        originalValue.time !== selectedTime

      if (isChanged) {
        updateButton.removeAttribute('disabled')
        return
      }

      updateButton.setAttribute('disabled', true)
    }

    const attachTimeSelector = (selector) => {
      timeContainer.innerHTML = ''
      timeContainer.append(selector)
      selector.addEventListener('change', syncUpdateButtonState)
      syncUpdateButtonState()
    }

    attachTimeSelector(timeSelector)
    syncUpdateButtonState()

    updateButton.addEventListener('click', async (e) => {
      e.preventDefault()
      e.target.setAttribute('disabled', true)
      const selectedDate = dateContainer.querySelector('select').value
      const selectedTime = timeContainer.querySelector('select').value

      if (
        originalValue.date === selectedDate &&
        originalValue.time === selectedTime
      ) {
        alert('お届け希望日時が変更されておりませんので、再度ご確認ください。')
        e.target.removeAttribute('disabled')
        return false
      }

      //build params
      const params = {
        o: id.toString(),
        // eslint-disable-next-line no-undef
        h: FFJP.hashedOrder,
        deliveryDate: selectedDate,
        deliveryTime: selectedTime,
        shippingDate: dateSelector.dataset.shipDate,
        productType: type,
      }

      try {
        await axios.post(`${baseUrlFrontApi}/updatedelivery`, params)

        alert('お届け希望日時を変更しました。')
        e.target.innerText = '変更済'
        dateSelector.setAttribute('disabled', true)
        timeContainer.querySelector('select').setAttribute('disabled', true)
      } catch (error) {
        console.error(error)

        if (error.error === 'SHIPPING_DATE_MINIMUM_ERROR') {
          alert(
            'お届けに間に合わない配送希望日が選択されているため、改めて配送希望日を選択し「変更する」ボタンを押してください。'
          )
        } else {
          alert('エラーが発生しました。時間をおいて再度お試しください。')
        }
        e.target.removeAttribute('disabled')
      }
    })

    dateSelector.addEventListener('change', (e) => {
      const selectedDate = e.target.value
      const selectedTime = timeContainer.querySelector('select').value
      const timeSelector = buildTimeSelector(
        dateTimeOptions,
        selectedDate,
        selectedTime
      )

      attachTimeSelector(timeSelector)
      syncUpdateButtonState()
    })
  })
}

const getDeliveryDateTimeOptions = async (
  globalThis,
  baseUrlFrontApi,
  ffServicesApiUrl,
  shippingAddress,
  carrier,
  type,
  isAirShippingDisabled
) => {
  const deliveryDateTimeOptionsBaseUrl = ffServicesApiUrl || baseUrlFrontApi
  const latestWarehouseArrivalDate =
    type === 'misc'
      ? globalThis.FFJP.mypageMiscLatestWarehouseArrivalDate
      : globalThis.FFJP.mypageFurnitureLatestWarehouseArrivalDate
  const isAllocated =
    type === 'misc'
      ? globalThis.FFJP.isMiscAllocated
      : globalThis.FFJP.isFurnitureAllocated

  const { data } = await axios.post(
    `${deliveryDateTimeOptionsBaseUrl}/delivery_date_time_options/specified_carriers`,
    {
      products: [
        {
          type,
          warehouseArrivalDate: latestWarehouseArrivalDate
            ? dayjs(latestWarehouseArrivalDate).format('YYYY-MM-DD')
            : null,
          isAirShippingDisabled: isAirShippingDisabled == 'true',
        },
      ],
      isPreOrder: !isAllocated,
      zip: shippingAddress.zip,
      prefectureCode: shippingAddress.province_code.replace('JP-', ''),
      address: shippingAddress.address1,
      city: shippingAddress.city,
      targetCarriers: {
        [type]: carrier,
      },
    }
  )
  return data
}

const buildDateSelector = function (
  dateTimeOptions, // { options: { date: string }[], deliveryTimeRange: string[]}
  selectedDate // string
) {
  const selectList = document.createElement('select')
  dateTimeOptions.options.forEach((o) => {
    const option = document.createElement('option')
    option.value = o.date
    // 先頭に「指定なし」等の文字列を含む場合の考慮
    option.text = dayjs(o.date, 'YYYY-MM-DD', true).isValid()
      ? dayjs(o.date).format('YYYY年MM月DD日') +
        `(${weekDays[dayjs(o.date).day()]})`
      : o.date
    option.dataset.value = o.date
    if (selectedDate == o.date) option.setAttribute('selected', 'selected')
    selectList.appendChild(option)
  })

  return selectList
}

const buildTimeSelector = function (
  dateTimeOptions, // { options: { isDeliveryTimeRangeSelectable: boolean }[], deliveryTimeRange: string[] }
  selectedDate, // string
  selectedTime // string
) {
  const { options, deliveryTimeRange } = dateTimeOptions

  const dateOptionMatch = options.find((o) => o.date === selectedDate)
  // 保存された配達希望日がAPIのレスポンスに含まれていなければ、0番目の options の配送時間帯を選択状態にする
  const dateOption = dateOptionMatch ? dateOptionMatch : options[0]
  const timeRange =
    !deliveryTimeRange.length ||
    !dateOption ||
    !dateOption.isDeliveryTimeRangeSelectable
      ? ['希望なし']
      : deliveryTimeRange

  const selectList = document.createElement('select')
  timeRange.forEach((time) => {
    const option = document.createElement('option')
    option.value = time
    option.text = time
    if (selectedTime == time) option.setAttribute('selected', 'selected')
    selectList.appendChild(option)
  })

  return selectList
}

const orderDetails = () => {
  if (!window.location.href.includes('account/orders/')) return false
  document.addEventListener('DOMContentLoaded', init)
}

export default orderDetails
